import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MirakalousLogo from '../../components/MirakalousLogo';
import ServiceDetailModal from './components/ServiceDetailModal';
import ElectricBorder from '../../components/ElectricBorder';

const EnterpriseAISolutions = () => {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      id: 'strategy',
      title: 'AI Strategy & Advisory',
      subtitle: 'What we do',
      description: 'Executive roadmaps, AI use‑case prioritization, ROI modeling; Responsible AI policy, model risk & governance, vendor/tool selection.',
      icon: 'Lightbulb',
      iconBg: 'bg-primary',
      features: [
        'Executive roadmaps, AI use‑case prioritization, ROI modeling',
        'Responsible AI policy, model risk & governance, vendor/tool selection'
      ]
    },
    {
      id: 'mlops',
      title: 'Data Platforms & MLOps',
      subtitle: 'What we do',
      description: 'Data lakes/lakehouse design; feature stores; batch/stream pipelines; ML lifecycle operations.',
      icon: 'GitBranch',
      iconBg: 'bg-success',
      features: [
        'Data lakes/lakehouse design; feature stores; batch/stream pipelines',
        'ML lifecycle: experiment tracking, CI/CD for ML, model registries, canary/blue‑green deploys'
      ]
    },
    {
      id: 'genai',
      title: 'Generative AI Solutions',
      subtitle: 'What we do',
      description: 'RAG & Enterprise Search; Agentic workflows; Summarization, Q&A, classification, content generation.',
      icon: 'Sparkles',
      iconBg: 'bg-accent',
      features: [
        'RAG & Enterprise Search (document intelligence, code search)',
        'Agentic Workflows (multi‑agent planners, tool‑use, function calling)',
        'Summarization, Q&A, classification, content generation across text/code'
      ]
    },
    {
      id: 'applied',
      title: 'Applied Modeling',
      subtitle: 'What we do',
      description: 'NLP, CV, time‑series/forecasting, recommenders; Fine‑tuning/distillation/quantization; safety layers.',
      icon: 'Layers',
      iconBg: 'bg-innovation-blue',
      features: [
        'NLP, computer vision, time‑series/forecasting, recommender systems',
        'Fine‑tuning, distillation, quantization, safety/guardrail layers'
      ]
    },
    {
      id: 'edge',
      title: 'On‑Prem & Edge AI',
      subtitle: 'What we do',
      description: 'Air‑gapped deployments; GPU sizing & clusters; Triton/TensorRT‑LLM, vLLM, TGI; edge inference.',
      icon: 'Server',
      iconBg: 'bg-steel-blue',
      features: [
        'Air‑gapped deployments; GPU sizing & cluster design',
        'NVIDIA Triton/TensorRT‑LLM, vLLM, TGI; edge inference on Jetson/mobile'
      ]
    },
    {
      id: 'modernization',
      title: 'Legacy Modernization with AI',
      subtitle: 'What we do',
      description: 'Code understanding & refactoring copilots; language migration; automated tests.',
      icon: 'Code',
      iconBg: 'bg-primary',
      features: [
        'Code understanding & refactoring copilots',
        'Language migration (e.g., COBOL→Java/Spring Boot), automated test generation'
      ]
    },
    {
      id: 'healthcare',
      title: 'Healthcare & Life Sciences AI',
      subtitle: 'What we do',
      description: 'PHI‑aware processing; imaging; digital twins & simulation‑ML; HIPAA controls.',
      icon: 'HeartPulse',
      iconBg: 'bg-success',
      features: [
        'PHI‑aware document processing, medical imaging, digital twins & simulation‑ML hybrids',
        'De‑identification pipelines and HIPAA‑aligned process controls'
      ]
    },
    {
      id: 'conversational',
      title: 'Conversational & Voice AI',
      subtitle: 'What we do',
      description: 'Private assistants; contact‑center augmentation; meeting copilots; retrieval‑grounded responses.',
      icon: 'Mic',
      iconBg: 'bg-accent',
      features: [
        'Private assistants (ASR/TTS/NLU), contact‑center augmentation, meeting copilots',
        'Retrieval‑grounded, policy‑constrained responses'
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics, BI & Decisioning',
      subtitle: 'What we do',
      description: 'Causal inference/experimentation; uplift modeling; enablement; realtime dashboards & HITL.',
      icon: 'BarChart3',
      iconBg: 'bg-primary',
      features: [
        'Causal inference/experimentation, uplift modeling, marketing & sales enablement',
        'Real‑time dashboards, human‑in‑the‑loop review workflows'
      ]
    }
  ];

  const handleLearnMore = (service) => {
    setSelectedService(service);
    setDetailModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      
      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary via-steel-blue to-accent text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <MirakalousLogo size={32} useImage={true} />
              <span className="text-lg font-medium">Enterprise AI Solutions</span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Design, build, and scale
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-foreground to-blue-200">
                applied AI in your enterprise
              </span>
            </h1>
            
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Simple, outcome‑driven services across strategy, platforms, generative AI, applied modeling,
              on‑prem/edge, modernization, healthcare, voice, and decisioning.
            </p>
            
            {/* Top CTAs removed per request */}
          </div>
        </div>
      </section>
      {/* What we do */}
      <section className="py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary">What we do</h2>
          </div>

          {/* Quick look: service categories */}
          <div className="mb-8 sticky top-16 z-10">
            <div className="rounded-xl bg-background/60 backdrop-blur-md border border-white/10 shadow-md overflow-x-auto">
              <div className="flex items-center gap-2 w-max min-w-full px-3 py-2">
                {services?.map((s) => (
                  <a
                    key={s?.id}
                    href={`#${s?.id}`}
                    className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 text-white text-sm font-semibold border border-white/15 hover:bg-white/15 hover:border-white/25 enterprise-transition whitespace-nowrap shadow-sm"
                  >
                    {s?.title}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12 items-stretch">
            {services?.map((service) => (
              <ElectricBorder key={service?.id} color="#5B7CFF" thickness={2} className="rounded-xl scroll-mt-24 h-full">
                <div id={service?.id} className="bg-card rounded-xl border border-border/60 p-5 enterprise-transition hover:shadow-xl h-full flex flex-col min-h-[360px]">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-xl ${service?.iconBg} shadow-md`}> 
                      <Icon name={service?.icon} size={28} color="white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-text-primary tracking-tight">{service?.title}</h3>
                      <p className="text-[11px] uppercase tracking-[0.15em] text-text-secondary">{service?.subtitle}</p>
                    </div>
                  </div>
                </div>
                <p className="text-text-secondary mb-4">{service?.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-auto">
                  {service?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 bg-surface rounded-lg p-3 border border-border/60 hover:border-accent/50 enterprise-transition">
                      <Icon name="Check" size={18} color="var(--color-success)" />
                      <span className="text-base text-text-primary/90 leading-snug">{feature}</span>
                    </div>
                  ))}
                </div>
                </div>
              </ElectricBorder>
            ))}
          </div>
        </div>
      </section>
      {/* Engagement Models */}
      <section className="py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">Engagement Models</h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">Choose how you want to partner with us. No pricing or timelines shown.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {[ 
              { title: 'Discovery & Solution Design', icon: 'Search' },
              { title: 'Fixed‑scope MVP', icon: 'Package' },
              { title: 'Dedicated Delivery Squad', icon: 'Users' },
              { title: 'Advisory Retainer', icon: 'Calendar' }
            ]?.map((item, idx) => (
              <ElectricBorder key={idx} color="#5B7CFF" thickness={1.5} className="rounded-xl h-full">
                <div className="bg-card/90 backdrop-blur p-6 text-center rounded-xl border border-border/60 shadow-sm enterprise-transition hover:shadow-md h-full flex flex-col items-center justify-center min-h-[180px]">
                  <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-lg bg-primary text-white shadow-md">
                    <Icon name={item.icon} size={20} color="white" />
                  </div>
                  <div className="font-semibold text-text-primary text-base md:text-lg text-center">{item.title}</div>
                </div>
              </ElectricBorder>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-accent text-white">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Transform Your Enterprise with AI?
          </h2>
          <p className="text-xl text-blue-100 mb-8">Book a discovery call, request a technical walkthrough, or see a live demo.</p>
          
          {/* Bottom CTAs removed per request */}
        </div>
      </section>
      
    </div>
  );
};

export default EnterpriseAISolutions;